/**
 * Iter 67ez — Vendor EasyPos Albania Fiscalization panel.
 *
 * Lets the vendor:
 *  - Enable / disable fiscalization
 *  - Save NUIS, business unit, operator + software codes
 *  - Generate the long-lived agent token (shown ONCE, copy-to-clipboard)
 *  - Download the relay agent Python script + see paste-able env config
 *  - See pending / succeeded / failed fiscalization jobs
 *
 * Pairs with: backend `/api/vendor/easypos/*` + agent endpoints. The actual
 * fiscalization happens on the vendor's local machine — this panel is the
 * marketplace-side control room.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Receipt, KeyRound, Download, Copy, RefreshCw, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

const STATUS_META = {
  pending:     { label: "Pending",     cls: "bg-amber-100 text-amber-800 border-amber-200" },
  in_progress: { label: "In progress", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  succeeded:   { label: "Fiscalized",  cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  failed:      { label: "Failed",      cls: "bg-rose-100 text-rose-800 border-rose-200" },
  cancelled:   { label: "Cancelled",   cls: "bg-neutral-100 text-neutral-700 border-neutral-200" },
};

function _copy(text) {
  if (!text) return;
  try { navigator.clipboard.writeText(text); toast.success("Copied"); }
  catch { toast.error("Could not copy — please select and copy manually"); }
}

function JobsTable({ jobs }) {
  if (!jobs?.length) {
    return <div className="text-sm text-neutral-500 py-6 text-center" data-testid="easypos-jobs-empty">
      No fiscalization jobs yet. They'll appear here once you click "Fiscalize" on a booking.
    </div>;
  }
  return (
    <div className="overflow-x-auto" data-testid="easypos-jobs-table">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-neutral-500 border-b">
          <tr>
            <th className="text-left py-2 pr-2">Status</th>
            <th className="text-left py-2 pr-2">Type</th>
            <th className="text-left py-2 pr-2">Created</th>
            <th className="text-left py-2 pr-2">NIVF / NSLF</th>
            <th className="text-left py-2 pr-2">Booking / Invoice</th>
            <th className="text-left py-2 pr-2">Error</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => {
            const m = STATUS_META[j.status] || STATUS_META.pending;
            const created = j.created_at ? new Date(j.created_at).toLocaleString() : "—";
            const ref = j.booking_id || j.invoice_id;
            return (
              <tr key={j.id} className="border-b last:border-b-0" data-testid={`easypos-job-row-${j.id}`}>
                <td className="py-2 pr-2"><Badge className={`${m.cls} border`} data-testid={`easypos-job-status-${j.id}`}>{m.label}</Badge></td>
                <td className="py-2 pr-2 font-mono text-xs">{j.job_type === "B2C_EASYPOS" ? "B2C" : "B2B"}</td>
                <td className="py-2 pr-2 text-xs text-neutral-600">{created}</td>
                <td className="py-2 pr-2 font-mono text-xs">
                  {j.result?.nivf || j.result?.nslf ? (
                    <div className="space-y-0.5">
                      {j.result?.nivf && <div>NIVF: <span className="font-semibold">{j.result.nivf}</span></div>}
                      {j.result?.nslf && <div>NSLF: <span className="font-semibold">{j.result.nslf}</span></div>}
                    </div>
                  ) : <span className="text-neutral-400">—</span>}
                </td>
                <td className="py-2 pr-2 font-mono text-xs text-neutral-600">{ref ? ref.slice(0, 8) : "—"}</td>
                <td className="py-2 pr-2 text-xs text-rose-700">{j.last_error || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function VendorEasyPosFiscalizationPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState({
    enabled: false,
    nuis: "",
    business_unit_code: "",
    operator_code: "",
    software_code: "",
    invoice_currency: "ALL",
    default_vat_code: "B",
  });
  const [newToken, setNewToken] = useState("");
  const [agentScript, setAgentScript] = useState("");

  async function refresh() {
    setLoading(true);
    try {
      const [cfg, jobsRes] = await Promise.all([
        api.get("/vendor/easypos/config"),
        api.get("/vendor/easypos/jobs?limit=50"),
      ]);
      setConfig(cfg.data);
      setForm({
        enabled: !!cfg.data.enabled,
        nuis: cfg.data.nuis || "",
        business_unit_code: cfg.data.business_unit_code || "",
        operator_code: cfg.data.operator_code || "",
        software_code: cfg.data.software_code || "",
        invoice_currency: cfg.data.invoice_currency || "ALL",
        default_vat_code: cfg.data.default_vat_code || "B",
      });
      setJobs(jobsRes.data.jobs || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  async function saveConfig() {
    setSaving(true);
    try {
      const res = await api.put("/vendor/easypos/config", form);
      setConfig(res.data);
      toast.success("Fiscalization config saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function regenerateToken() {
    if (!window.confirm("Regenerate the agent token? The previous token will stop working immediately.")) return;
    try {
      const res = await api.post("/vendor/easypos/regenerate-token");
      setNewToken(res.data.agent_token);
      toast.success("New token generated — copy it now, it won't be shown again");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function downloadAgentScript() {
    try {
      const res = await api.get("/easypos/agent-script", { responseType: "text" });
      const blob = new Blob([res.data], { type: "text/x-python" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "easypos_agent.py";
      a.click();
      URL.revokeObjectURL(url);
      setAgentScript(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center py-12 text-neutral-500" data-testid="easypos-loading">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading fiscalization config…
    </div>
  );

  return (
    <div className="space-y-6" data-testid="vendor-easypos-panel">
      {/* Intro / regulatory context */}
      <Card className="border-red-200 bg-gradient-to-br from-red-50 via-orange-50/30 to-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-900">
            <Receipt className="h-5 w-5" />
            Albanian Fiscalization (Law 87/2019)
          </CardTitle>
          <CardDescription className="text-red-800/80">
            Send every B2C receipt and B2B invoice through your local easyPos / easyInvoice software so it gets
            registered with the Albanian tax authority. We do not call your local server directly — you run a small
            <strong> relay agent</strong> on a machine inside your shop that polls us for pending jobs.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Step 1 — Config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Step 1 · Fiscalization config</CardTitle>
          <CardDescription>These values are printed on every fiscal receipt. They are NOT secret.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg bg-neutral-50 border">
            <div>
              <div className="text-sm font-semibold">Enable EasyPos fiscalization</div>
              <div className="text-xs text-neutral-500">When OFF, your bookings are not sent for fiscalization.</div>
            </div>
            <Switch
              checked={form.enabled}
              onCheckedChange={(v) => setForm({ ...form, enabled: !!v })}
              data-testid="easypos-toggle-enabled"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>NUIS (tax ID)</Label>
              <Input value={form.nuis} onChange={(e) => setForm({ ...form, nuis: e.target.value })}
                     placeholder="e.g. L64202808P" data-testid="easypos-input-nuis" />
            </div>
            <div>
              <Label>Business unit code</Label>
              <Input value={form.business_unit_code} onChange={(e) => setForm({ ...form, business_unit_code: e.target.value })}
                     placeholder="e.g. cx272hp639" data-testid="easypos-input-unit" />
            </div>
            <div>
              <Label>Operator code</Label>
              <Input value={form.operator_code} onChange={(e) => setForm({ ...form, operator_code: e.target.value })}
                     placeholder="e.g. wr859eh731" data-testid="easypos-input-operator" />
            </div>
            <div>
              <Label>Software code (optional)</Label>
              <Input value={form.software_code} onChange={(e) => setForm({ ...form, software_code: e.target.value })}
                     placeholder="e.g. RentalCar-1" data-testid="easypos-input-software" />
            </div>
            <div>
              <Label>Default VAT code</Label>
              <Input value={form.default_vat_code} onChange={(e) => setForm({ ...form, default_vat_code: e.target.value })}
                     placeholder="B" data-testid="easypos-input-vatcode" />
            </div>
            <div>
              <Label>Invoice currency</Label>
              <Input value={form.invoice_currency} onChange={(e) => setForm({ ...form, invoice_currency: e.target.value.toUpperCase().slice(0, 3) })}
                     placeholder="ALL" data-testid="easypos-input-currency" />
            </div>
          </div>
          <Button onClick={saveConfig} disabled={saving} data-testid="easypos-save-config">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save fiscalization config
          </Button>
        </CardContent>
      </Card>

      {/* Step 2 — Agent token */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-4 w-4" /> Step 2 · Relay agent token</CardTitle>
          <CardDescription>
            The relay agent on your local machine uses this token to authenticate. Token is shown ONCE; if you lose
            it, regenerate a new one.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between p-3 rounded-lg bg-neutral-50 border">
            <div>
              <div className="text-sm font-semibold">Current token</div>
              <div className="text-xs text-neutral-500 font-mono">
                {config?.agent_token_masked || <span className="italic text-neutral-400">none yet — generate one below</span>}
              </div>
            </div>
            <Button variant="outline" onClick={regenerateToken} data-testid="easypos-regenerate-token">
              <RefreshCw className="h-4 w-4 mr-2" />
              {config?.agent_token_set ? "Regenerate" : "Generate token"}
            </Button>
          </div>
          {newToken && (
            <div className="p-4 rounded-lg border-2 border-amber-300 bg-amber-50" data-testid="easypos-new-token-banner">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-bold text-amber-900 text-sm">Copy this token now — it will NOT be shown again</div>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 p-2 rounded bg-white border border-amber-300 font-mono text-xs break-all" data-testid="easypos-new-token-value">{newToken}</code>
                    <Button size="sm" onClick={() => _copy(newToken)} data-testid="easypos-copy-token"><Copy className="h-3 w-3" /></Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 3 — Relay agent download */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Download className="h-4 w-4" /> Step 3 · Run the relay agent</CardTitle>
          <CardDescription>
            Download the Python script, set the env variables, and run it on a machine inside your shop LAN
            (the same network as your easyPos installation).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button variant="outline" onClick={downloadAgentScript} data-testid="easypos-download-agent">
            <Download className="h-4 w-4 mr-2" /> Download <code className="ml-1 font-mono text-xs">easypos_agent.py</code>
          </Button>
          <div className="p-3 rounded-lg bg-neutral-900 text-neutral-100 font-mono text-xs leading-relaxed overflow-x-auto" data-testid="easypos-agent-env-snippet">
            <div className="text-neutral-400"># Set env vars (paste your token + local easyPos auth string)</div>
            <div>export BC_AGENT_TOKEN="paste-your-token-here"</div>
            <div>export BC_EASYPOS_URL="http://127.0.0.1:9000/v1"</div>
            <div>export BC_EASYPOS_AUTH="your-easypos-authorization-string"</div>
            <div>python3 easypos_agent.py</div>
          </div>
          <div className="text-xs text-neutral-500">
            The agent polls every 10s by default. Adjust with <code className="px-1 py-0.5 rounded bg-neutral-100">BC_POLL_INTERVAL</code>.
          </div>
        </CardContent>
      </Card>

      {/* Jobs ledger */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Fiscalization jobs</CardTitle>
            <CardDescription>Last 50 fiscalization attempts. Refreshes when you reload this tab.</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={refresh} data-testid="easypos-refresh-jobs">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <JobsTable jobs={jobs} />
        </CardContent>
      </Card>
    </div>
  );
}
